import { useState, useCallback, useEffect } from "react";
import { EmployeeApiProvider } from "./employeeApi";
import EmployeeBottomNav from "./EmployeeBottomNav";
import { FULL_LOGO_URL } from "@/components/site/Logo";
import { initials } from "./prefs";
import HomeScreen from "./screens/HomeScreen";
import TasksScreen from "./screens/TasksScreen";
import CalendarScreen from "./screens/CalendarScreen";
import TeamScreen from "./screens/TeamScreen";
import MaterialsScreen from "./screens/MaterialsScreen";
import SettingsScreen from "./screens/SettingsScreen";
import MoreScreen from "./screens/MoreScreen";
import JobDetailScreen from "./screens/JobDetailScreen";

const MORE_TABS = new Set(["mere", "materialer", "indstillinger"]);

// Both entry points use the same real employee APIs and permission checks.
export default function EmployeeApp({ client, onLogout, tabKey = "msh_emp_tab" }) {
    const [tab, setTab] = useState(() => sessionStorage.getItem(tabKey) || "hjem");
    const [jobId, setJobId] = useState(null);
    const [me, setMe] = useState(null);

    useEffect(() => { sessionStorage.setItem(tabKey, tab); }, [tab, tabKey]);
    useEffect(() => { client.getProfile().then(setMe).catch(() => {}); }, [client]);

    const openJob = useCallback((id) => setJobId(id), []);
    const closeJob = useCallback(() => setJobId(null), []);
    const go = useCallback((t) => { setJobId(null); setTab(t); }, []);

    const activeNav = jobId ? "opgaver" : MORE_TABS.has(tab) ? "mere" : tab;

    let content;
    if (jobId) content = <JobDetailScreen jobId={jobId} onBack={closeJob} />;
    else if (tab === "opgaver") content = <TasksScreen onOpenJob={openJob} />;
    else if (tab === "kalender") content = <CalendarScreen onOpenJob={openJob} />;
    else if (tab === "team") content = <TeamScreen />;
    else if (tab === "mere") content = <MoreScreen onNavigate={go} />;
    else if (tab === "materialer") content = <MaterialsScreen onBack={() => go("mere")} onGoTasks={() => go("opgaver")} />;
    else if (tab === "indstillinger") content = <SettingsScreen onBack={() => go("mere")} onLogout={onLogout} />;
    else content = <HomeScreen onOpenJob={openJob} onGoTasks={() => go("opgaver")} />;

    return (
        <EmployeeApiProvider client={client}>
            <div className="h-full w-full flex flex-col bg-slate-50 overflow-hidden" data-testid="employee-app">
                <header className="shrink-0 bg-white border-b border-slate-200/70 flex items-center justify-center px-4 py-2 relative">
                    <div className="flex flex-col items-center leading-none min-w-0 max-w-[65%]">
                        <img src={FULL_LOGO_URL} alt="MyServiceHelper" className="h-6 w-auto object-contain select-none" draggable={false} />
                        <span className="text-[11px] text-slate-500 mt-1 truncate max-w-full" data-testid="employee-company-name">{me?.company_name || "Medarbejder"}</span>
                    </div>
                    <button onClick={() => go("indstillinger")} data-testid="header-avatar"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center active:scale-95 transition-transform">
                        {initials(me?.name) || "?"}
                    </button>
                </header>
                <main className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="employee-scroll">
                    <div className="w-full max-w-md mx-auto px-4 py-4">{content}</div>
                </main>
                <EmployeeBottomNav active={activeNav} onNavigate={go} />
            </div>
        </EmployeeApiProvider>
    );
}
